import React, { memo, useState } from 'react'
import styled from 'styled-components'
import cn, { Argument as ClassName } from 'classnames'
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link, useSearchParams } from 'react-router-dom'
import { pickBy } from 'lodash'

import { sortByString } from '../../../lib/sort-by-string'
import { Table, TableBody, TableBodyCell, TableHeader, TableHeaderCell, TableRow } from '../../../lib/table'
import { WidgetLayout } from '../../../lib/widget-layout'
import Text from '../../../ui/Text'
import { useChargePointGetChargePointsQuery } from '../../../common/redux/api/web-api'
import ChargePointExpandOptions from '../../../lib/chargePointExpandOptions'
import SettingsInfo from '../../../ui/chargepoints/SettingsInfo'
import { ChargepointStatus } from '../../../lib/chargepoint-status'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { ChargePointsSearchWidget } from '../../features/charge-points-search-widget'
import { ChargersPagination } from '../../../lib/chargers-pagination'
import { ChargePointPageFilterStatus } from '../../types/ChargePointsPageFilterStatus'
import ListPageHeader from '../../../common/features/ListPageHader/ListPageHeader'

export type ChargePointsPageProps = {
    hideButtons?: boolean
    className?: ClassName
    style?: React.CSSProperties
}

enum SortParameter {
    ID = 'id',
    NAME = 'name',
    STATUS = 'chargePointStatus',
    ORGANISATION = 'companyName',
}

// pick only allowed fields
const mapSearchParamsToFilter = ({
    id,
    companyId,
    status,
    name,
    ownerEmail,
    imsiNumber,
    installedFirmwareVersion,
    ownerName,
    ownerMobile,
    ocppOnly,
    partnerId,
    productId,
}: Partial<ChargePointPageFilterStatus>): Partial<ChargePointPageFilterStatus> => {
    return {
        id,
        companyId,
        status,
        name,
        ownerEmail,
        imsiNumber,
        installedFirmwareVersion,
        ownerName,
        ownerMobile,
        ocppOnly,
        partnerId,
        productId,
    }
}

export const SIcon = styled(FontAwesomeIcon)`
    margin-left: 4px;
`

export const ChargePointsPage = memo(function ChargePointsPage({
    className,
    style,
}: ChargePointsPageProps): React.ReactElement {
    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; reversed: boolean }>({
        parameter: SortParameter.ID,
        reversed: true,
    })

    const [searchParams, setParams] = useSearchParams()
    const params = Object.fromEntries(searchParams.entries())
    const filter = mapSearchParamsToFilter(params)

    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(20)

    const { currentData: chargePointsData, isFetching: dataLoading } = useChargePointGetChargePointsQuery({
        id: filter.id,
        name: filter.name,
        status: filter.status,
        companyId: filter.companyId,
        imsiNumber: filter.imsiNumber,
        installedFirmwareVersion: filter.installedFirmwareVersion,
        partnerId: filter.partnerId,
        productId: filter.productId,
        ownerName: filter.ownerName,
        ownerEmail: filter.ownerEmail,
        ownerMobile: filter.ownerMobile,
        ocppOnly: filter.ocppOnly,
        page: currentPage,
        noOfItems: pageSize,
        expand: ChargePointExpandOptions.OcppConfig,
    })

    const filterSubmit = (filter: Partial<ChargePointPageFilterStatus>) => {
        setParams(pickBy(filter, (value) => value !== undefined && value !== null))
        if (currentPage !== 0) setCurrentPage(0)
    }

    return (
        <PageLayoutContainer>
            <WidgetLayout
                data-testid="table-charge-points"
                className={cn('flex-1', className)}
                style={style}
                headerButtons={<ChargePointsSearchWidget onSubmit={filterSubmit} filter={filter} />}
            >
                <ListPageHeader
                    amount={chargePointsData?.totalCount}
                    isLoading={dataLoading}
                    pageSize={pageSize}
                    setCurrentPage={setCurrentPage}
                    setPageSize={setPageSize}
                />
                <Table className="flex-1" isLoading={dataLoading}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell data-testid="th-charge-points-serial-number">
                                <a
                                    data-testid="table-th-serial-link"
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.ID,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                    className="pointer"
                                >
                                    <Text code="lblSerialNumberShort"></Text>
                                    {sortFilter?.parameter === SortParameter.ID && (
                                        <SIcon
                                            data-testid="icon-sn"
                                            icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                        />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="th-charge-points-status">
                                <a
                                    data-testid="table-th-charge-points-name-link"
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.STATUS,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                    className="pointer"
                                >
                                    <Text code="lblStatus"></Text>
                                    {sortFilter?.parameter === SortParameter.STATUS && (
                                        <SIcon
                                            data-testid="icon-sn"
                                            icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                        />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="th-charge-points-name">
                                <a
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.NAME,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                    className="pointer"
                                >
                                    <Text code="lblName"></Text>
                                    {sortFilter?.parameter === SortParameter.NAME && (
                                        <SIcon
                                            data-testid="icon-sn"
                                            icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                        />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="th-charge-points-organisation">
                                <a
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.ORGANISATION,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                    className="pointer"
                                >
                                    <Text code="lblOrganisation"></Text>
                                    {sortFilter?.parameter === SortParameter.ORGANISATION && (
                                        <SIcon
                                            data-testid="icon-sn"
                                            icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                        />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="th-charge-points-wifi-pwd">
                                <Text code="lblSetWifiPassword"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="th-charge-points-settings">
                                <Text code="lblSettings"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="th-charge-points-fw-version">
                                <Text code="lblFWVersion"></Text>
                            </TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody isLoading={dataLoading} numberOfLoadingColumns={7}>
                        {Array.from(chargePointsData?.items ?? [])
                            .sort((a, b) =>
                                sortByString(
                                    (a[sortFilter.parameter] as string) ?? '',
                                    (b[sortFilter.parameter] as string) ?? '',
                                    sortFilter.reversed
                                )
                            )
                            .map((cp) => (
                                <TableRow key={cp.id} data-testid="table-row">
                                    <TableBodyCell>
                                        <Link
                                            className="pointer table btn-link"
                                            data-testid={`tb-${cp.id}`}
                                            to={`/chargepoints/${cp.id}`}
                                        >
                                            {cp.id}
                                        </Link>
                                    </TableBodyCell>
                                    <TableBodyCell>
                                        <ChargepointStatus chargePoint={cp} />
                                    </TableBodyCell>
                                    <TableBodyCell data-testid={`tb-${cp.name}`}>{cp.name}</TableBodyCell>
                                    <TableBodyCell data-testid={`tb-${cp.companyId}`}>{cp.companyName}</TableBodyCell>
                                    <TableBodyCell data-testid="tb-wifipwd">{cp.wifiPassword}</TableBodyCell>
                                    <TableBodyCell data-testid="tb-settingsinfo">
                                        <SettingsInfo chargePoint={cp} />
                                    </TableBodyCell>

                                    <TableBodyCell data-testid="tb-fwVersion">
                                        {cp.installedFirmwareVersion}
                                    </TableBodyCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                {chargePointsData && (chargePointsData?.totalCount ?? 0) > 0 && (
                    <ChargersPagination
                        setPageIndex={setCurrentPage}
                        totalCount={chargePointsData?.totalCount ?? 0}
                        pageSize={pageSize}
                        pageIndex={currentPage}
                        displayedChargers={pageSize}
                    />
                )}
            </WidgetLayout>
        </PageLayoutContainer>
    )
})
